/**
 * The permission ids of the portal (backend `Orbita.SharedKernel.Security.Permissions`, docs/modules/identity.md "Roles and
 * permissions"). A role is a named set of them; administrators hold all. The ids are stable: roles store them and the audit
 * trail shows them.
 */
export const PERMISSIONS = {
  identity: {
    usersManage: 'identity.users.manage',
    offboarding: 'identity.offboarding',
    structureEdit: 'identity.structure.edit',
    invitationsManage: 'identity.invitations.manage',
    securityManage: 'identity.security.manage',
    policyManage: 'identity.policy.manage',
    rolesAssign: 'identity.roles.assign',
    rolesManage: 'identity.roles.manage',
  },
  admin: {
    settings: 'admin.settings',
    auditRead: 'admin.audit.read',
    systemRead: 'admin.system.read',
    jobsRun: 'admin.jobs.run',
    infrastructureManage: 'admin.infrastructure.manage',
    messagingManage: 'admin.messaging.manage',
  },
  search: { reindex: 'search.reindex' },
  files: { cleanup: 'files.cleanup' },
  wiki: { manage: 'wiki.manage' },
  calendar: { roomsManage: 'calendar.rooms.manage' },
  tasks: { settingsManage: 'tasks.settings.manage' },
  messenger: { settingsManage: 'messenger.settings.manage' },
  disk: { settingsManage: 'disk.settings.manage' },
  lists: { settingsManage: 'lists.settings.manage' },
  bizproc: { webhooksManage: 'bizproc.webhooks.manage', settingsManage: 'bizproc.settings.manage' },
} as const;

/** Every permission id, in the order of the matrix. */
export const ALL_PERMISSION_IDS: readonly string[] = Object.values(PERMISSIONS).flatMap((group) =>
  Object.values(group),
);

/** The key of a permission id in the locale files (`admin:permissions.<key>.title`): dots become underscores. */
export const permissionKey = (id: string): string => id.replaceAll('.', '_');

/** Permissions a person may be given for some departments only (backend `PermissionInfo.Scopable`). */
export const SCOPABLE_PERMISSIONS: readonly string[] = [
  PERMISSIONS.identity.usersManage,
  PERMISSIONS.identity.offboarding,
];

/** The permissions of the sections of the administration area (`/admin/…`); the area opens for anyone holding one of them. */
export const ADMIN_AREA_PERMISSIONS: readonly string[] = [
  PERMISSIONS.admin.settings,
  PERMISSIONS.identity.policyManage,
  PERMISSIONS.admin.auditRead,
  PERMISSIONS.admin.systemRead,
  PERMISSIONS.admin.infrastructureManage,
  PERMISSIONS.admin.messagingManage,
  PERMISSIONS.search.reindex,
  PERMISSIONS.files.cleanup,
  PERMISSIONS.disk.settingsManage,
  PERMISSIONS.identity.rolesManage,
  PERMISSIONS.identity.rolesAssign,
];
